import type { JSX } from 'react';

import { devicesSummary, visibleHealthIssues } from '../../lib/revealStaging';
import type { RevealHealthIssue } from '../../lib/revealStaging';
import type { VesselDevice } from '../../lib/useBoatDevices';

// Stage 2 — "What's aboard". Renders the device inventory mirrored up by the
// boat. Quality floor (task 7.2): `displayName` is always set server-side
// (ADR 0052 resolution chain), and the summary copy degrades gracefully when
// devices are still reporting in — a sparse boat still reads as understood,
// never a raw zero.
//
// Also renders a quiet "network notes" block for the boat's network-health
// issues (FTUE↔cloud parity B2) — read-only: correction stays on the
// existing gateway-pick controls (the Gateways stage). Never blocks the
// device list, never shows an empty state — it renders nothing when there's
// nothing worth surfacing (visibleHealthIssues drops 'info' and empty input).

const PROTOCOL_LABELS: Record<string, string> = {
  n2k: 'NMEA 2000',
  n0183: 'NMEA 0183',
  venus: 'Victron GX',
  starlink: 'Starlink',
  sk: 'Signal K',
};

const HEALTH_SEVERITY_LABELS: Record<string, string> = {
  critical: 'Critical',
  warning: 'Warning',
};

export interface RevealAboardProps {
  devices: VesselDevice[];
  deviceCount: number;
  candidateCount: number;
  loading: boolean;
  error: string | null;
  healthIssues: RevealHealthIssue[];
}

function deviceSubtitle(d: VesselDevice): string {
  const parts: string[] = [];
  const mfr = d.manufacturer?.name ?? undefined;
  const model = d.productInfo?.modelId ?? undefined;
  if (mfr && model) parts.push(`${mfr} ${model}`);
  else if (mfr) parts.push(mfr);
  else if (model) parts.push(model);
  const fn = d.deviceFunction?.name ?? undefined;
  if (fn && !parts.some((p) => p.toLowerCase().includes(fn.toLowerCase()))) parts.push(fn);
  return parts.join(' · ');
}

// The single muted "type" line under a device name: protocol first (so the kind
// is never lost) then any mfr/model/function detail. Keeps the device row to two
// quiet lines — a scannable inventory, not a tile.
function deviceMeta(d: VesselDevice): string {
  const proto = PROTOCOL_LABELS[d.protocol] ?? d.protocol;
  const rest = deviceSubtitle(d);
  return rest !== '' ? `${proto} · ${rest}` : proto;
}

export function RevealAboard({
  devices,
  deviceCount,
  candidateCount,
  loading,
  error,
  healthIssues,
}: RevealAboardProps): JSX.Element {
  const summary = devicesSummary(deviceCount, candidateCount);
  const visibleIssues = visibleHealthIssues(healthIssues);

  return (
    <div className="reveal-stage reveal-aboard" data-testid="reveal-stage-aboard">
      {/* Honest live found-count: the title carries the real device count and is
          announced as it settles (aria-live). Under the reorder the inventory is
          usually already complete by the time this renders, so this is a reveal of
          real devices, not live-discovery theater (reveal-decision-flow task 4.1). */}
      <h3 className="reveal-stage__title" aria-live="polite" data-testid="reveal-aboard-count">
        {summary.title}
      </h3>
      <p className="reveal-stage__sub">{summary.sub}</p>

      {error !== null ? (
        <ul className="form-errors" role="alert">
          <li>{error}</li>
        </ul>
      ) : devices.length > 0 ? (
        <>
          <ul className="reveal-device-grid" data-testid="reveal-device-grid">
            {devices.map((d) => (
              <li className="reveal-device" key={d.deviceId}>
                <span className="reveal-device__name">{d.displayName}</span>
                <span className="reveal-device__meta">{deviceMeta(d)}</span>
              </li>
            ))}
          </ul>
          {/* Ambient "still listening" (③): device discovery has no definitive
              end — devices report whenever they have data — so this never claims
              "done"; it stays a calm, always-listening note under the list. */}
          <p className="reveal-listening" aria-live="polite" data-testid="reveal-aboard-listening">
            <span className="reveal-listening__pulse" aria-hidden="true" />
            Still listening — anything new will appear here as it reports in.
          </p>
        </>
      ) : loading ? (
        <p className="reveal-muted" aria-live="polite">
          Reading the devices behind your gateways…
        </p>
      ) : (
        // Sparse floor: gateways present but no devices enumerated yet — still
        // a confident, understood state (never "0 devices").
        <p className="reveal-muted">
          Your network is connected. We’ll list each device here as it reports in — some only speak
          up when they have something to say.
        </p>
      )}

      {visibleIssues.length > 0 ? (
        <ul className="reveal-health" data-testid="reveal-health-issues">
          {visibleIssues.map((issue) => (
            <li
              className="reveal-health__item"
              key={issue.id}
              data-testid={`reveal-health-issue-${issue.id}`}
            >
              <div className="reveal-health__head">
                <span className={`reveal-alarm__sev reveal-alarm__sev--${issue.severity}`}>
                  {HEALTH_SEVERITY_LABELS[issue.severity] ?? issue.severity}
                </span>
                <span className="reveal-health__title">{issue.title}</span>
              </div>
              <p className="reveal-health__action">{issue.recommendedAction}</p>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
